import type { GscSourceStatus } from './api/gscConnectionApi';

// Only the known reasons short-circuit; a null reason (older visibility) falls
// back to statusMessage rather than reading as "not token_revoked".
function isRevoked(source: GscSourceStatus): boolean {
  if (source.status !== 'DISCONNECTED') return false;
  if (source.disconnectReason === 'token_revoked') return true;
  if (
    source.disconnectReason === 'user' ||
    source.disconnectReason === 'replaced' ||
    source.disconnectReason === 'access_lost'
  ) {
    return false;
  }
  return (source.statusMessage ?? null) !== null;
}

function isAccessLost(source: GscSourceStatus): boolean {
  return source.status === 'DISCONNECTED' && source.disconnectReason === 'access_lost';
}

// Sync stamps are UTC ISO strings from the same API, so they order lexically.
function laterOf(a: string | null | undefined, b: string | null | undefined): string | null {
  return (b ?? '') > (a ?? '') ? (b ?? null) : (a ?? null);
}

function normalizedAccountEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase();
  return normalized ? normalized : null;
}

export type GscConnectionState = {
  /** Live, broken, revoked, or access-lost properties of the connected account. */
  domains: string[];
  /** Occupies a plan slot: GSC, not DISCONNECTED, has a propertyUrl. */
  liveDomains: string[];
  lastSyncAt: string | null;
  /** Latest sync among revoked holding-slot domains. Null when nothing is broken. */
  brokenSinceAt: string | null;
  isBroken: boolean;
  /** Properties Google stopped serving (`access_lost`) that no live row has replaced. */
  lostUrls: string[];
  /** Latest sync among `lostUrls`. */
  lostSinceAt: string | null;
};

type DomainState = {
  isLive: boolean;
  isRevoked: boolean;
  isLost: boolean;
  holdsSlot: boolean;
  lastSyncAt: string | null;
};

function gscSourcesForAccount(
  sources: GscSourceStatus[],
  connectedAccountEmail: string | null | undefined
): Array<GscSourceStatus & { propertyUrl: string }> {
  const gsc = sources.filter(
    (source): source is GscSourceStatus & { propertyUrl: string } =>
      source.type === 'GSC' && source.propertyUrl !== null
  );
  const wanted = normalizedAccountEmail(connectedAccountEmail);
  if (wanted === null) {
    return gsc;
  }
  if (!gsc.some((source) => normalizedAccountEmail(source.connectedAccountEmail) !== null)) {
    return gsc;
  }
  return gsc.filter((source) => normalizedAccountEmail(source.connectedAccountEmail) === wanted);
}

/**
 * Reduces a tenant's sources to the single connection the Integrations card
 * shows.
 *
 * Grouped by property because activation creates a source per domain per
 * connect cycle, so one domain can own a live source plus husks of earlier
 * cycles — and a husk must not contradict the source that replaced it.
 */
export function readGscConnectionState(
  sources: GscSourceStatus[],
  connectedAccountEmail?: string | null
): GscConnectionState {
  const byDomain = new Map<string, DomainState>();
  for (const source of gscSourcesForAccount(sources, connectedAccountEmail)) {
    const live = source.status === 'ACTIVE';
    const revoked = isRevoked(source);
    const lost = isAccessLost(source);
    const holdsSlot = source.status !== 'DISCONNECTED';
    const seen = byDomain.get(source.propertyUrl);
    byDomain.set(source.propertyUrl, {
      isLive: (seen?.isLive ?? false) || live,
      isRevoked: (seen?.isRevoked ?? false) || revoked,
      isLost: (seen?.isLost ?? false) || lost,
      holdsSlot: (seen?.holdsSlot ?? false) || holdsSlot,
      lastSyncAt:
        holdsSlot || revoked || lost
          ? laterOf(seen?.lastSyncAt, source.lastSyncAt)
          : (seen?.lastSyncAt ?? null),
    });
  }

  const entries = Array.from(byDomain.entries());
  const holdingSlot = entries.filter(([, domain]) => domain.holdsSlot || domain.isRevoked);
  const lost = entries.filter(([, domain]) => domain.isLost && !domain.isLive);
  const shown = entries.filter(
    ([, domain]) => domain.holdsSlot || domain.isRevoked || (domain.isLost && !domain.isLive)
  );
  return {
    domains: shown.map(([propertyUrl]) => propertyUrl),
    liveDomains: holdingSlot.filter(([, domain]) => domain.holdsSlot).map(([propertyUrl]) => propertyUrl),
    lastSyncAt: shown.reduce<string | null>((latest, [, domain]) => laterOf(latest, domain.lastSyncAt), null),
    brokenSinceAt: holdingSlot
      .filter(([, domain]) => !domain.isLive && domain.isRevoked)
      .reduce<string | null>((latest, [, domain]) => laterOf(latest, domain.lastSyncAt), null),
    // `some`, not `every`: one account means one set of credentials, so the
    // domains that still look live just have not run their daily sync yet.
    isBroken: holdingSlot.some(([, domain]) => !domain.isLive),
    lostUrls: lost.map(([propertyUrl]) => propertyUrl),
    lostSinceAt: lost.reduce<string | null>((latest, [, domain]) => laterOf(latest, domain.lastSyncAt), null),
  };
}
